import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { FullPageLoader } from '@/components/feedback/States'
import { portalHome } from '@/features/auth/types'
import { useAuth } from '@/features/auth/useAuth'
import { publicAdmissionsQuery, publicHomepageQuery } from '@/features/homepage/api'
import { describePage } from '@/features/homepage/blocks'
import { HomepageView } from '@/features/homepage/components/HomepageView'
import type { PublicHomepage } from '@/features/homepage/types'
import { siteContextQuery } from '@/features/site/context'

/**
 * "/": the school's public homepage on its own address when the school switched it on; the
 * sign-in page otherwise (no homepage, single-address mode, website or console addresses).
 */
export function SchoolHomePage() {
  const site = useQuery(siteContextQuery())
  const isSchool = site.data?.kind === 'school'
  const homepage = useQuery({ ...publicHomepageQuery(), enabled: isSchool })

  if (site.isPending || (isSchool && homepage.isPending)) return <FullPageLoader />
  if (!isSchool || !homepage.data?.enabled) return <Navigate to="/login" replace />
  return <PublicPage page={homepage.data} websiteUrl={site.data?.websiteUrl ?? null} />
}

function PublicPage({ page, websiteUrl }: { page: PublicHomepage; websiteUrl: string | null }) {
  const { t } = useTranslation('homepage')
  const { status, portal } = useAuth()
  const admissions = useQuery(publicAdmissionsQuery())

  useEffect(() => {
    const previousTitle = document.title
    document.title = page.heroTitle ?? page.school.name
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    const previousDescription = meta?.content
    if (meta) meta.content = describePage(page)
    return () => {
      document.title = previousTitle
      if (meta && previousDescription !== undefined) meta.content = previousDescription
    }
  }, [page])

  const account =
    status === 'authenticated' && portal ? { href: portalHome[portal], label: t('My space') } : undefined

  return (
    <HomepageView
      page={page}
      // A failed request hides admissions rather than the page.
      admissions={admissions.isError ? null : admissions.data}
      account={account}
      websiteUrl={websiteUrl}
    />
  )
}
